import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatError } from "@/lib/api";
import { PageTitle } from "./AdminApp";
import { PrizesEditor } from "./PrizesEditor";

const ICONS = ["drum", "culture", "community", "food", "music", "heart", "star"];

const Section = ({ title, children }) => (
  <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 sm:p-6">
    <h2 className="mb-5 font-display text-xl text-gold">{title}</h2>
    <div className="space-y-4">{children}</div>
  </section>
);

const F = ({ label, value, onChange, area, testid, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {area ? (
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="min-h-[100px] border-neutral-800 bg-neutral-950" data-testid={testid} />
    ) : (
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-neutral-800 bg-neutral-950" data-testid={testid} />
    )}
    {hint && <p className="text-xs text-neutral-500">{hint}</p>}
  </div>
);

export default function ContentEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/content").then((r) => setC(r.data));
  }, []);
  if (!c) return <p className="text-neutral-500">Loading…</p>;

  const set = (k) => (v) => setC({ ...c, [k]: v });
  const setItem = (list, i, k) => (v) => setC({ ...c, [list]: c[list].map((x, j) => (j === i ? { ...x, [k]: v } : x)) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", c);
      setC(data);
      toast.success("Content saved — live on the public page");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content-editor" className="pb-24">
      <PageTitle title="Content" />
      <div className="grid gap-5 xl:grid-cols-2">
        <Section title="Hero & general">
          <F label="Event title" value={c.event_title} onChange={set("event_title")} testid="content-event-title" />
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Subtitle (compound)" value={c.event_subtitle} onChange={set("event_subtitle")} testid="content-event-subtitle" />
            <F label="Year" value={c.year} onChange={set("year")} testid="content-year" />
          </div>
          <F label="Tagline" value={c.tagline} onChange={set("tagline")} testid="content-tagline" />
          <F label="Dates label" value={c.dates_label} onChange={set("dates_label")} testid="content-dates-label" />
          <F label="Countdown target" value={c.countdown_target} onChange={set("countdown_target")} testid="content-countdown-target" hint="ISO format, e.g. 2026-10-30T10:00:00-04:00" />
          <F label="Intro" area value={c.intro} onChange={set("intro")} testid="content-intro" />
          <F label="Footer note" value={c.footer_note} onChange={set("footer_note")} testid="content-footer-note" />
        </Section>

        <Section title="Schedule">
          {c.schedule.map((s, i) => (
            <div key={s.day} className="space-y-3 rounded-md border border-neutral-800 p-4" data-testid={`content-schedule-${s.day}`}>
              <p className="text-xs text-neutral-500">{s.day}</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <F label="Date label" value={s.date_label} onChange={setItem("schedule", i, "date_label")} testid={`content-schedule-${i}-label`} />
                <F label="Title" value={s.title} onChange={setItem("schedule", i, "title")} testid={`content-schedule-${i}-title`} />
                <F label="Time" value={s.time} onChange={setItem("schedule", i, "time")} testid={`content-schedule-${i}-time`} />
              </div>
              <F label="Description" value={s.description} onChange={setItem("schedule", i, "description")} testid={`content-schedule-${i}-description`} />
            </div>
          ))}
        </Section>

        <Section title="Location & contact">
          <F label="Location name" value={c.location_name} onChange={set("location_name")} testid="content-location-name" />
          <F label="Address" value={c.location_address} onChange={set("location_address")} testid="content-location-address" />
          <F label="Google Maps link" value={c.maps_url} onChange={set("maps_url")} testid="content-maps-url" />
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Contact name" value={c.contact_name} onChange={set("contact_name")} testid="content-contact-name" />
            <F label="Contact phone" value={c.contact_phone} onChange={set("contact_phone")} testid="content-contact-phone" />
          </div>
          <F label="Contact note" value={c.contact_note} onChange={set("contact_note")} testid="content-contact-note" />
        </Section>

        <Section title="Highlights">
          {c.highlights.map((h, i) => (
            <div key={i} className="space-y-3 rounded-md border border-neutral-800 p-4">
              <div className="flex items-end gap-2">
                <div className="flex-1"><F label="Title" value={h.title} onChange={setItem("highlights", i, "title")} testid={`content-highlight-${i}-title`} /></div>
                <select value={h.icon} onChange={(e) => setItem("highlights", i, "icon")(e.target.value)} className="h-9 rounded-md border border-neutral-800 bg-neutral-950 px-2 text-sm" data-testid={`content-highlight-${i}-icon`}>
                  {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <button onClick={() => setC({ ...c, highlights: c.highlights.filter((_, j) => j !== i) })} className="h-9 rounded-md px-2 text-neutral-500 hover:text-red-400" data-testid={`content-highlight-${i}-remove`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <F label="Description" value={h.description} onChange={setItem("highlights", i, "description")} testid={`content-highlight-${i}-description`} />
            </div>
          ))}
          <button onClick={() => setC({ ...c, highlights: [...c.highlights, { icon: "star", title: "New highlight", description: "" }] })} className="flex items-center gap-2 text-sm font-semibold text-amber-400" data-testid="content-highlight-add">
            <Plus className="h-4 w-4" /> Add highlight
          </button>
        </Section>

        <Section title="Registration acknowledgment">
          <F label="Acknowledgment text (shown on registration; stored with each record)" area value={c.acknowledgment_text} onChange={set("acknowledgment_text")} testid="content-ack-text" />
        </Section>

        <Section title="Donations & giveaway">
          <F label="Stripe Payment Link URL" value={c.donation_url} onChange={set("donation_url")} testid="content-donation-url" hint="Donors are sent here with ?client_reference_id=…&prefilled_email=…. Leave empty to pause online donations." />
          <F label="Donate-now intro" area value={c.donation_intro} onChange={set("donation_intro")} testid="content-donation-intro" />
          <F label="Giveaway rules (one rule per line)" area value={c.giveaway_rules} onChange={set("giveaway_rules")} testid="content-giveaway-rules" />
          
        </Section>

        <Section title="Weekend giveaway prizes">
          <PrizesEditor prizes={c.prizes} onChange={set("prizes")} />
        </Section>
      </div>
      <div className="fixed bottom-4 right-4 z-40 sm:bottom-6 sm:right-8">
        <button onClick={save} disabled={saving} className="flex h-12 items-center gap-2 rounded-full bg-amber-500 px-6 font-display text-lg font-semibold text-neutral-950 shadow-[0_0_24px_rgba(245,158,11,0.35)] transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="content-save-button">
          <Save className="h-5 w-5" /> {saving ? "Saving…" : "Save content"}
        </button>
      </div>
    </div>
  );
}
